import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from "@material-ui/core/styles";
import { useHistory } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import { getAdhkar, deleteAdhkar, reorderAdhkar } from '../../services/tasbeehService';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Box, makeStyles, CircularProgress, Snackbar, Tooltip, Typography, Chip
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import AddIcon from '@material-ui/icons/Add';
import TasbeehFormModal from './TasbeehFormModal';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  tableContainer: {
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    background: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingText: {
    marginTop: 16,
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  actionBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  },
  deleteBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.1)',
      color: '#ef4444',
    },
  },
  orderBadge: {
    background: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    padding: '4px 8px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  arabicCell: {
    fontFamily: '"Traditional Arabic", "Scheherazade New", serif',
    fontSize: '1.1rem',
    direction: 'rtl',
  },
  countChip: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.85rem',
    height: 36,
    padding: '0 8px',
    borderRadius: 10,
  },
}));

export default function TasbeehListPage() {
  const classes = useStyles();
  
  const theme = useTheme();
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedAdhkarId, setSelectedAdhkarId] = useState(null);

  const handleOpenCreateModal = () => {
    setSelectedAdhkarId(null);
    setFormModalOpen(true);
  };

  const handleOpenEditModal = (adhkarId) => {
    setSelectedAdhkarId(adhkarId);
    setFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setFormModalOpen(false);
    setSelectedAdhkarId(null);
  };

  const handleFormSuccess = () => {
    setSnackbar({
      open: true,
      message: selectedAdhkarId ? 'যিকির সফলভাবে আপডেট করা হয়েছে!' : 'নতুন যিকির সফলভাবে তৈরি করা হয়েছে!'
    });
    loadData({ page });
  };

  const loadData = useCallback(async (opts = {}) => {
    setLoading(true);
    const targetPage = opts.page !== undefined ? opts.page : 1;
    try {
      const result = await getAdhkar({ page: targetPage });
      setItems(result.items);
      setTotalCount(result.total);
      setTotalPages(result.totalPages);
      setPage(result.page);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load adhkar' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData({ page: 1 }); }, [loadData]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadData({ page: newPage });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAdhkar(deleteTarget.id);
      setSnackbar({ open: true, message: 'Adhkar removed successfully' });
      setDeleteTarget(null);
      loadData({ page });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete adhkar' });
    }
  };

  const handleReorder = async (item, direction) => {
    const idx = items.indexOf(item);
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    const target = items[targetIdx];
    try {
      await Promise.all([
        reorderAdhkar(item.id, target.orderIndex),
        reorderAdhkar(target.id, item.orderIndex),
      ]);
      loadData({ page });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to reorder' });
    }
  };

  return (
    <AdminLayout title="Tasbeeh / Adhkar">
      <div className={classes.toolbar}>
        <Typography variant="body2" color="textSecondary">
          Manage dhikr phrases displayed in the Tasbeeh counter
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 16 }} flexWrap="wrap">
          <Chip
            label={`Total Adhkar: ${totalCount}`}
            className={classes.countChip}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateModal}
            disableElevation
          >
            Add New Dhikr
          </Button>
        </Box>
      </div>

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} thickness={4} />
          <Typography className={classes.loadingText}>Loading records...</Typography>
        </div>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="80">Order</TableCell>
                <TableCell width="25%">Name (English)</TableCell>
                <TableCell width="25%">Name (Bangla)</TableCell>
                <TableCell width="25%">Arabic</TableCell>
                <TableCell width="80">Target</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <span className={classes.orderBadge}>{item.orderIndex}</span>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textPrimary" style={{ fontWeight: 500 }}>
                      {item.name?.en || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
                      {item.name?.bn || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.arabicCell}>
                    {item.arabic || '-'}
                  </TableCell>
                  <TableCell>
                    <span className={classes.orderBadge}>{item.target ?? 33}</span>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" style={{ gap: 6 }}>
                      <Tooltip title="Move Up">
                        <IconButton className={classes.actionBtn} onClick={() => handleReorder(item, 'up')}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move Down">
                        <IconButton className={classes.actionBtn} onClick={() => handleReorder(item, 'down')}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton className={classes.actionBtn} onClick={() => handleOpenEditModal(item.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton className={classes.deleteBtn} onClick={() => setDeleteTarget(item)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: "48px 0" }} color="textSecondary">
                    No adhkar found. Add your first dhikr.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && totalCount > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={20}
          onPageChange={handlePageChange}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Dhikr"
        message="Are you sure you want to permanently remove this dhikr? This action cannot be undone."
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <TasbeehFormModal
        open={formModalOpen}
        adhkarId={selectedAdhkarId}
        onClose={handleCloseFormModal}
        onSuccess={handleFormSuccess}
      />

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </AdminLayout>
  );
}
